// DrawPathTool — click-to-add-waypoints path drawing with configurable width
// Click adds waypoint, double-click or Enter finishes (min 2 points), ESC cancels, Backspace removes last

import * as THREE from 'three'
import type { Tool, ToolContext } from '@/types/editor'
import type { ToolManager } from '../ToolManager'
import type { DimensionOverlay } from '@/ui/DimensionOverlay'
import { DPM } from '@/constants/coordinates'
import { generatePathPolygon, pathLength } from '@/utils/pathGeometry'
import { calculatePolygonAreaFromPoints } from '@/utils/areaCalculation'
import { getViewer, getToolMode } from '@/ui/state'
import { debug } from '@/utils/debug'

export interface DrawPathConfig {
  /** Called when drawing finishes. Returns new shape ID. */
  onCreate: (path: {
    x: number; y: number
    width: number; height: number
    points: Array<{ x: number; y: number }>
    pathWaypoints: Array<{ x: number; y: number }>
    pathWidth: number
  }) => string
  /** Preview color (default: 0x4ecca3) */
  previewColor?: number
  /** Initial path width in dots (default: 50 = 1m) */
  initialWidth?: number
  /** Optional dimension overlay for live measurements */
  overlay?: DimensionOverlay
}

const MAX_WAYPOINTS = 50

export class DrawPathTool implements Tool {
  readonly name = 'draw-path'
  readonly cursor = 'crosshair'
  readonly hint = 'tool.hint.drawPath'

  private toolManager: ToolManager
  private config: DrawPathConfig
  private pathWidth: number

  // Waypoints in editor coords (dots, absolute)
  private waypoints: Array<{ x: number; y: number }> = []
  // Current mouse position in world coords (for rubberband)
  private currentWorld: THREE.Vector3 | null = null
  // Camera reference for overlay projection
  private lastCamera: THREE.Camera | null = null

  // Preview
  private previewGroup: THREE.Group
  private previewFill: THREE.Mesh | null = null
  private previewLine: THREE.Line | null = null

  constructor(toolManager: ToolManager, scene: THREE.Scene, config: DrawPathConfig) {
    this.toolManager = toolManager
    this.config = config
    this.pathWidth = config.initialWidth ?? 50

    this.previewGroup = new THREE.Group()
    this.previewGroup.name = 'draw-path-preview'
    scene.add(this.previewGroup)
  }

  private contextMenuHandler = (e: Event) => e.preventDefault()

  activate(): void {
    this.waypoints = []
    this.currentWorld = null
    const selMgr = getViewer()?.getScene()?.getSelectionManager()
    selMgr?.deselect()
    selMgr?.clearHover()
    selMgr?.setEnabled(false)
    // Suppress context menu so right-click can finish path
    document.addEventListener('contextmenu', this.contextMenuHandler)
    debug.log('DrawPathTool', 'Activated')
  }

  deactivate(): void {
    this.cancelDrawing()
    document.removeEventListener('contextmenu', this.contextMenuHandler)
    if (getToolMode() === 'pointer') {
      getViewer()?.getScene()?.getSelectionManager()?.setEnabled(true)
    }
    debug.log('DrawPathTool', 'Deactivated')
  }

  onMouseDown(ctx: ToolContext): void {
    // Right-click finishes the path
    if (ctx.event.button === 2) {
      if (this.waypoints.length >= 2) {
        ctx.event.preventDefault()
        this.finishPath()
      }
      return
    }
    if (ctx.event.button !== 0) return
    if (!ctx.worldPoint || !ctx.editorPoint) return

    if (this.waypoints.length >= MAX_WAYPOINTS) {
      this.finishPath()
      ctx.event.preventDefault()
      return
    }

    // Double-click finishes the path (don't add duplicate waypoint)
    if (ctx.event.detail >= 2 && this.waypoints.length >= 2) {
      this.finishPath()
      ctx.event.preventDefault()
      return
    }

    // Add waypoint
    this.waypoints.push({ x: ctx.editorPoint.x, y: ctx.editorPoint.y })
    this.updatePreview()
    ctx.event.preventDefault()
    ctx.event.stopPropagation()

    debug.log('DrawPathTool', `Added waypoint ${this.waypoints.length} at (${ctx.editorPoint.x}, ${ctx.editorPoint.y})`)
  }

  onMouseMove(ctx: ToolContext): void {
    if (!ctx.worldPoint) return
    this.currentWorld = ctx.worldPoint.clone()
    this.lastCamera = ctx.camera

    if (this.waypoints.length > 0) {
      this.updatePreview()
    }

    this.toolManager.setCursor(this.cursor)
  }

  onMouseUp(_ctx: ToolContext): void {
    // Click handling is in onMouseDown
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.cancelDrawing()
      this.toolManager.setActiveTool('select')
    } else if (event.key === 'Backspace') {
      if (this.waypoints.length > 0) {
        this.waypoints.pop()
        this.updatePreview()
        debug.log('DrawPathTool', `Removed last waypoint, ${this.waypoints.length} remaining`)
      }
    } else if (event.key === 'Enter') {
      if (this.waypoints.length >= 2) {
        this.finishPath()
      }
    }
  }

  dispose(): void {
    this.cancelDrawing()
    this.previewGroup.parent?.remove(this.previewGroup)
  }

  // --- Private ---

  private finishPath(): void {
    if (this.waypoints.length < 2) return

    // Compute centroid of waypoints
    let cx = 0, cy = 0
    for (const v of this.waypoints) {
      cx += v.x
      cy += v.y
    }
    cx /= this.waypoints.length
    cy /= this.waypoints.length
    cx = Math.round(cx)
    cy = Math.round(cy)

    // Waypoints relative to centroid
    const relativeWaypoints = this.waypoints.map(v => ({
      x: v.x - cx,
      y: v.y - cy,
    }))

    // Generate corridor outline from relative waypoints
    const outlinePoints = generatePathPolygon(relativeWaypoints, this.pathWidth)

    // Bounding box of outline
    const xs = outlinePoints.map(p => p.x)
    const ys = outlinePoints.map(p => p.y)
    const width = Math.max(...xs) - Math.min(...xs)
    const height = Math.max(...ys) - Math.min(...ys)

    const id = this.config.onCreate({
      x: cx,
      y: cy,
      width,
      height,
      points: outlinePoints,
      pathWaypoints: relativeWaypoints,
      pathWidth: this.pathWidth,
    })

    debug.log('DrawPathTool', `Created path: ${id} with ${this.waypoints.length} waypoints`)

    // Auto-select the created path
    if (id) {
      getViewer()?.selectAfterRender(id)
    }

    this.cancelDrawing()
    this.toolManager.setActiveTool('select')
  }

  private cancelDrawing(): void {
    this.waypoints = []
    this.currentWorld = null
    this.lastCamera = null
    this.clearPreview()
    this.config.overlay?.hideAll()
  }

  private updatePreview(): void {
    this.clearPreview()

    if (this.waypoints.length === 0) return

    const color = this.config.previewColor ?? 0x4ecca3
    const y = 0.02

    // Build preview waypoints including rubberband point
    const previewWaypoints = [...this.waypoints]
    if (this.currentWorld) {
      previewWaypoints.push({
        x: this.currentWorld.x * DPM,
        y: this.currentWorld.z * DPM,
      })
    }

    // Generate corridor polygon
    if (previewWaypoints.length >= 2) {
      const outline = generatePathPolygon(previewWaypoints, this.pathWidth)

      if (outline.length >= 3) {
        // Fill preview
        const worldOutline = outline.map(p => new THREE.Vector3(p.x / DPM, y, p.y / DPM))

        const shape = new THREE.Shape()
        shape.moveTo(worldOutline[0].x, -worldOutline[0].z)
        for (let i = 1; i < worldOutline.length; i++) {
          shape.lineTo(worldOutline[i].x, -worldOutline[i].z)
        }
        shape.closePath()

        const fillGeometry = new THREE.ShapeGeometry(shape)
        const fillMaterial = new THREE.MeshBasicMaterial({
          color,
          transparent: true,
          opacity: 0.2,
          side: THREE.DoubleSide,
          depthWrite: false,
        })

        this.previewFill = new THREE.Mesh(fillGeometry, fillMaterial)
        this.previewFill.rotation.x = -Math.PI / 2
        this.previewFill.position.y = y
        this.previewGroup.add(this.previewFill)

        // Outline line
        const linePoints = [...worldOutline, worldOutline[0]]
        const lineGeometry = new THREE.BufferGeometry().setFromPoints(linePoints)
        const lineMaterial = new THREE.LineBasicMaterial({
          color,
          transparent: true,
          opacity: 0.8,
        })
        this.previewLine = new THREE.Line(lineGeometry, lineMaterial)
        this.previewGroup.add(this.previewLine)
      }

      // Centerline (dashed)
      const centerWorldPoints = previewWaypoints.map(p =>
        new THREE.Vector3(p.x / DPM, y + 0.01, p.y / DPM)
      )
      const centerGeometry = new THREE.BufferGeometry().setFromPoints(centerWorldPoints)
      const centerMaterial = new THREE.LineDashedMaterial({
        color,
        transparent: true,
        opacity: 0.5,
        dashSize: 0.15,
        gapSize: 0.1,
      })
      const centerLine = new THREE.Line(centerGeometry, centerMaterial)
      centerLine.computeLineDistances()
      this.previewGroup.add(centerLine)
    }

    // Waypoint dots
    const dotGeometry = new THREE.SphereGeometry(0.08, 8, 8)
    const dotMaterial = new THREE.MeshBasicMaterial({ color })
    for (const v of this.waypoints) {
      const dot = new THREE.Mesh(dotGeometry, dotMaterial)
      dot.position.set(v.x / DPM, y + 0.05, v.y / DPM)
      this.previewGroup.add(dot)
    }

    // Show live measurement overlays
    if (this.config.overlay && this.lastCamera && this.currentWorld) {
      const overlay = this.config.overlay
      const camera = this.lastCamera

      // Edge length: total path length
      const totalLen = pathLength(previewWaypoints) / DPM // in meters
      // Show at midpoint of last segment
      const lastWp = this.waypoints[this.waypoints.length - 1]
      const midX = (lastWp.x / DPM + this.currentWorld.x) / 2
      const midZ = (lastWp.y / DPM + this.currentWorld.z) / 2
      overlay.showEdge(totalLen, midX, midZ, camera)

      // Area of corridor
      if (previewWaypoints.length >= 2) {
        const outline = generatePathPolygon(previewWaypoints, this.pathWidth)
        if (outline.length >= 3) {
          const area = calculatePolygonAreaFromPoints(outline)
          let acx = 0, acy = 0
          for (const p of previewWaypoints) {
            acx += p.x / DPM
            acy += p.y / DPM
          }
          acx /= previewWaypoints.length
          acy /= previewWaypoints.length
          overlay.showArea(area, acx, acy, camera)
        }
      } else {
        overlay.hideSlot('area')
      }

      overlay.hideSlot('dimensions')
    }
  }

  private clearPreview(): void {
    while (this.previewGroup.children.length > 0) {
      const child = this.previewGroup.children[0]
      this.previewGroup.remove(child)
      if (child instanceof THREE.Mesh) {
        child.geometry?.dispose()
        if (child.material instanceof THREE.Material) {
          child.material.dispose()
        }
      } else if (child instanceof THREE.Line) {
        child.geometry?.dispose()
        if (child.material instanceof THREE.Material) {
          child.material.dispose()
        }
      }
    }
    this.previewFill = null
    this.previewLine = null
  }
}
